import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';

interface FooterData {
  aboutText: string;
  contactEmail: string;
  contactPhone: string;
  contactAddress: string;
  members: string;
  activities: string;
  socialLinks: {
    facebook?: string;
    twitter?: string;
    instagram?: string;
    youtube?: string;
  };
}

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [footerData, setFooterData] = useState<FooterData>({
    aboutText: 'Serving the community since 2016 with sports, activities, and a welcoming hub for all.',
    contactEmail: 'info@leftleysportsclub.org.uk',
    contactPhone: '07940 464809',
    contactAddress: 'Leftley Community Hub, Leicester',
    members: '500+',
    activities: '5+',
    socialLinks: {}
  });

  useEffect(() => {
    const fetchFooterData = async () => {
      try {
        const response = await axios.get('http://localhost:5000/api/homepage');
        const data = response.data.data || response.data;
        
        setFooterData({
          aboutText: data.footer?.text || 'Serving the community since 2016 with sports, activities, and a welcoming hub for all.',
          contactEmail: data.contact?.email || 'info@leftleysportsclub.org.uk',
          contactPhone: data.contact?.phone || '07940 464809',
          contactAddress: data.contact?.address || 'Leftley Community Hub, Leicester',
          members: data.stats?.members || '500+',
          activities: data.stats?.activities || '5+',
          socialLinks: data.contact?.socialMedia || {}
        });
      } catch (error) {
        console.error('Error fetching footer data:', error);
        // Keep default values if fetch fails
      }
    };

    fetchFooterData();
  }, []);

  return (
    <footer className="bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* About Section */}
          <div>
            <h3 className="text-xl font-bold mb-4">
              <span className="text-white">Leftley</span>
              <span className="text-secondary"> Sports Club</span>
            </h3>
            <p className="text-gray-400 text-sm">
              {footerData.aboutText}
            </p>
            <div className="mt-4">
              <p className="text-sm font-semibold text-secondary">{footerData.members} Members</p>
              <p className="text-sm font-semibold text-secondary">{footerData.activities} Activities Weekly</p>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/" className="text-gray-400 hover:text-secondary transition duration-300">
                  Home
                </Link>
              </li>
              <li>
                <Link to="/membership" className="text-gray-400 hover:text-secondary transition duration-300">
                  Become a Member
                </Link>
              </li>
              <li>
                <Link to="/community-hub" className="text-gray-400 hover:text-secondary transition duration-300">
                  Hire the Hub
                </Link>
              </li>
            </ul>
          </div>

          {/* Activities */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Activities</h4>
            <ul className="space-y-2 text-sm">
              <li>
                <Link to="/activities/football" className="text-gray-400 hover:text-secondary transition duration-300">
                  Football
                </Link>
              </li>
              <li>
                <Link to="/activities/walk-and-talk" className="text-gray-400 hover:text-secondary transition duration-300">
                  Walk & Talk
                </Link>
              </li>
              <li>
                <Link to="/activities/womens-coffee-mornings" className="text-gray-400 hover:text-secondary transition duration-300">
                  Women's Coffee Mornings
                </Link>
              </li>
              <li>
                <Link to="/activities/womens-aerobics" className="text-gray-400 hover:text-secondary transition duration-300">
                  Women's Aerobics
                </Link>
              </li>
              <li>
                <Link to="/activities/cycling" className="text-gray-400 hover:text-secondary transition duration-300">
                  Cycling
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h4 className="text-lg font-semibold mb-4">Contact Us</h4>
            <ul className="space-y-2 text-sm text-gray-400">
              <li>
                <a href={`mailto:${footerData.contactEmail}`} className="hover:text-secondary transition duration-300">
                  {footerData.contactEmail}
                </a>
              </li>
              <li>
                <a href={`tel:${footerData.contactPhone.replace(/\s/g, '')}`} className="hover:text-secondary transition duration-300">
                  {footerData.contactPhone}
                </a>
              </li>
              <li className="pt-2">
                <p className="text-xs whitespace-pre-line">{footerData.contactAddress}</p>
              </li>
            </ul>
            
            {/* Social Media Links */}
            {(footerData.socialLinks.facebook || footerData.socialLinks.twitter || 
              footerData.socialLinks.instagram || footerData.socialLinks.youtube) && (
              <div className="mt-4 flex space-x-3">
                {footerData.socialLinks.facebook && (
                  <a href={footerData.socialLinks.facebook} target="_blank" rel="noopener noreferrer" 
                     className="text-gray-400 hover:text-secondary transition duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                    </svg>
                  </a>
                )}
                {footerData.socialLinks.twitter && (
                  <a href={footerData.socialLinks.twitter} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-secondary transition duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/>
                    </svg>
                  </a>
                )}
                {footerData.socialLinks.instagram && (
                  <a href={footerData.socialLinks.instagram} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-secondary transition duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                    </svg>
                  </a>
                )}
                {footerData.socialLinks.youtube && (
                  <a href={footerData.socialLinks.youtube} target="_blank" rel="noopener noreferrer"
                     className="text-gray-400 hover:text-secondary transition duration-300">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                    </svg>
                  </a>
                )}
              </div>
            )}
          </div>
        </div>

        <div className="border-t border-gray-800 mt-8 pt-8 text-center text-sm text-gray-400">
          <p>&copy; {currentYear} Leftley Sports Club. All rights reserved.</p>
          <p className="mt-2">
            <Link to="/admin" className="hover:text-secondary transition duration-300">
              Admin Login
            </Link>
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
